import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Mail, User, Eye, EyeOff, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminLogin = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [adminExists, setAdminExists] = useState(true);
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: ''
  });
  
  const navigate = useNavigate();

  useEffect(() => {
    checkExistingAuth();
    checkAdminExists();
  }, []);

  const checkExistingAuth = async () => {
    const token = localStorage.getItem('admin_token');
    if (token) {
      // Verify token is still valid
      try {
        const res = await fetch(`${API_URL}/api/auth/me`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (res.ok) {
          navigate('/admin');
        } else {
          localStorage.removeItem('admin_token');
          localStorage.removeItem('admin_user');
        }
      } catch (error) {
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_user');
      }
    }
    setCheckingAuth(false);
  };

  const checkAdminExists = async () => {
    try {
      const res = await fetch(`${API_URL}/api/auth/check`);
      if (res.ok) {
        const data = await res.json();
        setAdminExists(data.admin_exists);
        if (!data.admin_exists) {
          setIsLogin(false); // Show register form if no admin exists
        }
      }
    } catch (error) {
      console.error('Error checking admin:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const endpoint = isLogin ? '/api/auth/login' : '/api/auth/register';
      const body = isLogin 
        ? { email: formData.email, password: formData.password }
        : formData;

      const res = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      // Read response as text first to avoid "body already consumed" issues
      const responseText = await res.text();
      
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        throw new Error(responseText || 'Erreur de connexion au serveur');
      }

      if (!res.ok) {
        // Handle validation errors from Pydantic
        const errorMsg = typeof data.detail === 'string' 
          ? data.detail 
          : Array.isArray(data.detail) 
            ? data.detail.map(e => e.msg).join(', ')
            : 'Une erreur est survenue';
        throw new Error(errorMsg);
      }

      // Store token and user info
      localStorage.setItem('admin_token', data.access_token);
      localStorage.setItem('admin_user', JSON.stringify(data.admin));

      toast.success(isLogin ? 'Connexion réussie !' : 'Compte créé avec succès !');
      navigate('/admin');
    } catch (error) {
      toast.error(error.message || 'Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  if (checkingAuth) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="w-12 h-12 border-2 border-accent/30 border-t-accent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-6">
      {/* Background effects */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-accent/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-orange-500/5 rounded-full blur-3xl" />
      </div>

      <div className="relative w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-white">
            <span style={{ color: '#FF6B00' }}>W</span>ebysee
            <span className="text-accent">.</span>
          </h1>
          <p className="text-white/50 mt-2">Espace Administration</p>
        </div>

        {/* Form Card */}
        <div className="bg-white/[0.02] border border-white/10 p-8">
          <h2 className="text-xl font-semibold text-white mb-6">
            {isLogin ? 'Connexion' : 'Créer un compte admin'}
          </h2>

          {!adminExists && (
            <div className="mb-6 p-4 bg-accent/10 border border-accent/20 text-accent text-sm">
              <p className="font-medium">Première configuration</p>
              <p className="text-accent/80 mt-1">Créez votre compte administrateur pour accéder au tableau de bord.</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <div>
                <label className="block text-white/60 text-sm mb-2">Nom</label>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" size={18} />
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Votre nom"
                    required={!isLogin}
                    className="w-full bg-white/5 border border-white/10 pl-12 pr-4 py-3 text-white placeholder:text-white/40 focus:outline-none focus:border-accent transition-colors"
                  />
                </div>
              </div>
            )}

            <div>
              <label className="block text-white/60 text-sm mb-2">Email</label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" size={18} />
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="admin@webysee.fr"
                  required
                  className="w-full bg-white/5 border border-white/10 pl-12 pr-4 py-3 text-white placeholder:text-white/40 focus:outline-none focus:border-accent transition-colors"
                />
              </div>
            </div>

            <div>
              <label className="block text-white/60 text-sm mb-2">Mot de passe</label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" size={18} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="••••••••"
                  required
                  minLength={6}
                  className="w-full bg-white/5 border border-white/10 pl-12 pr-12 py-3 text-white placeholder:text-white/40 focus:outline-none focus:border-accent transition-colors"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40 hover:text-white transition-colors"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {!isLogin && (
                <p className="text-white/40 text-xs mt-1">Minimum 6 caractères</p>
              )}
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-accent text-black py-3 font-medium hover:bg-accent/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2 mt-6"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
              ) : (
                <>
                  {isLogin ? 'Se connecter' : 'Créer le compte'}
                  <ArrowRight size={18} />
                </>
              )}
            </button>
          </form>

          {adminExists && (
            <div className="mt-6 text-center">
              <button
                onClick={() => setIsLogin(!isLogin)}
                className="text-white/50 hover:text-accent text-sm transition-colors"
              >
                {isLogin ? "Pas encore de compte ? S'inscrire" : 'Déjà un compte ? Se connecter'}
              </button>
            </div>
          )}
        </div>

        {/* Back to site */}
        <div className="text-center mt-6">
          <a
            href="/"
            className="text-white/40 hover:text-white text-sm transition-colors"
          >
            ← Retour au site
          </a>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
